import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";

const CHANNEL_ICON = { sms: "📱", email: "📧" };

const text = {
  en: {
    channel: { sms: "Text me a code", email: "Email me a code" },
    verifyTitle: "Verify It's You",
    chooseChannel: "How would you like to receive your code?",
    sentVia: (channel) => `We sent a code via ${channel === "sms" ? "SMS" : "email"} to `,
    codeLabel: "Verification Code",
    expiresIn: (time) => `Expires in ${time}`,
    expired: "Code expired — request a new one",
    verifying: "Verifying...",
    verify: "Verify & Sign In",
    back: "← Back",
    resend: "Resend code",
    welcome: "Welcome Back",
    signInTo: "Sign in to your ShadowVault",
    email: "Email Address",
    password: "Password",
    signingIn: "Signing in...",
    signIn: "Sign In",
    forgot: "Forgot password?",
    noAccount: "Don't have an account?",
    createOne: "Create one now",
    unexpected: "Unexpected response from server.",
    loginFailed: "Login failed",
    sendFailed: (channel) => `Couldn't send a code via ${channel}`,
    incorrectCode: "Incorrect code",
  },
  zh: {
    channel: { sms: "短信发送验证码", email: "邮件发送验证码" },
    verifyTitle: "验证身份",
    chooseChannel: "您希望通过哪种方式接收验证码？",
    sentVia: (channel) => `验证码已通过${channel === "sms" ? "短信" : "邮件"}发送至 `,
    codeLabel: "验证码",
    expiresIn: (time) => `${time} 后过期`,
    expired: "验证码已过期，请重新获取",
    verifying: "验证中...",
    verify: "验证并登录",
    back: "← 返回",
    resend: "重新发送",
    welcome: "欢迎回来",
    signInTo: "登录您的 ShadowVault",
    email: "邮箱地址",
    password: "密码",
    signingIn: "登录中...",
    signIn: "登录",
    forgot: "忘记密码？",
    noAccount: "还没有账户？",
    createOne: "立即注册",
    unexpected: "服务器响应异常。",
    loginFailed: "登录失败",
    sendFailed: () => "验证码发送失败",
    incorrectCode: "验证码错误",
  },
};

export default function Login({ setToken, setUser }) {
  const { lang } = useOutletContext();
  const t = text[lang] || text.en;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const loginCalledRef = useRef(false); // prevent double call in StrictMode

  // "credentials" -> "channel" (only if >1 option) -> "otp"
  const [stage, setStage] = useState("credentials");
  const [pendingToken, setPendingToken] = useState(null);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [otp, setOtp] = useState(null); // { channel, maskedDestination, expiresInSeconds } | null
  const [code, setCode] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);

  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Countdown shown next to the code input so users know when it expires.
  useEffect(() => {
    if (stage !== "otp" || secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [stage, secondsLeft]);

  const sendOtp = async (channel, token) => {
    const res = await axios.post(`${API_BASE_URL}/api/auth/login/send-otp`, {
      pendingToken: token || pendingToken,
      channel,
    });
    setOtp(res.data);
    setSecondsLeft(res.data.expiresInSeconds || 120);
    setCode("");
    setStage("otp");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading || loginCalledRef.current) return; // prevent multiple calls
    setError("");
    setLoading(true);
    loginCalledRef.current = true;

    try {
      const res = await axios.post(`${API_BASE_URL}/api/auth/login`, { email, password });

      if (!res.data.passwordValid) {
        setError(t.unexpected);
        return;
      }

      const channels = res.data.availableChannels || [];
      setPendingToken(res.data.pendingToken);
      setAvailableChannels(channels);

      if (channels.length <= 1) {
        // Only one way to receive a code — skip the redundant choice screen.
        await sendOtp(channels[0] || "email", res.data.pendingToken);
      } else {
        setStage("channel");
      }
    } catch (err) {
      setError(err.response?.data?.message || t.loginFailed);
    } finally {
      setLoading(false);
      loginCalledRef.current = false;
    }
  };

  const handleChooseChannel = async (channel) => {
    setError("");
    setLoading(true);
    try {
      await sendOtp(channel);
    } catch (err) {
      setError(err.response?.data?.message || t.sendFailed(channel));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/auth/login/verify-otp`, {
        pendingToken,
        code,
      });

      if (!res.data.user) {
        // Should never happen (the backend always includes it on 200), but
        // JSON.stringify(undefined) silently produces the literal string
        // "undefined" instead of throwing - which then crashes App.js's
        // JSON.parse on the next load. Fail loudly here instead of
        // writing a value that breaks the whole app three steps later.
        throw new Error("Login response was missing user data — please try again");
      }

      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));

      setToken(res.data.token);
      setUser(res.data.user);

      navigate("/"); // redirect to main/dashboard
    } catch (err) {
      setError(err.response?.data?.message || t.incorrectCode);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => handleChooseChannel(otp.channel);

  const handleBackToCredentials = () => {
    setStage("credentials");
    setOtp(null);
    setCode("");
    setError("");
  };

  const handleBackToChannel = () => {
    setStage("channel");
    setOtp(null);
    setCode("");
    setError("");
  };

  if (stage === "channel") {
    return (
      <div className="w-full max-w-md bg-dark-card border border-dark-border rounded-xl p-8 shadow-lg space-y-5">
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-white">{t.verifyTitle}</h2>
          <p className="text-gray-400 mt-2">{t.chooseChannel}</p>
        </div>

        <div className="space-y-3">
          {availableChannels.map((ch) => (
            <button
              key={ch}
              type="button"
              onClick={() => handleChooseChannel(ch)}
              disabled={loading}
              className="w-full py-3 rounded-lg font-semibold transition text-lg bg-dark-bg border border-dark-border hover:border-primary text-white disabled:opacity-50"
            >
              {CHANNEL_ICON[ch] || "🔑"} {t.channel[ch] || ch}
            </button>
          ))}
        </div>

        {error && <p className="text-red-400 text-sm text-center bg-red-500 bg-opacity-10 border border-red-500 rounded-lg p-3">{error}</p>}

        <button type="button" onClick={handleBackToCredentials} className="text-sm text-gray-400 hover:text-primary transition font-medium">
          {t.back}
        </button>
      </div>
    );
  }

  if (stage === "otp") {
    return (
      <form
        onSubmit={handleVerifyOtp}
        className="w-full max-w-md bg-dark-card border border-dark-border rounded-xl p-8 shadow-lg space-y-5"
      >
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-white">{t.verifyTitle}</h2>
          <p className="text-gray-400 mt-2">
            {t.sentVia(otp.channel)}
            <span className="text-gray-300">{otp.maskedDestination}</span>
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">{t.codeLabel}</label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="w-full px-4 py-2 bg-dark-bg border border-dark-border rounded-lg focus:outline-none focus:border-primary text-white placeholder-gray-500 text-center text-2xl tracking-[0.5em]"
            placeholder="000000"
            required
          />
          <p className="text-xs text-gray-500 mt-2 text-center">
            {secondsLeft > 0
              ? t.expiresIn(`${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}`)
              : t.expired}
          </p>
        </div>

        {error && <p className="text-red-400 text-sm text-center bg-red-500 bg-opacity-10 border border-red-500 rounded-lg p-3">{error}</p>}

        <button
          type="submit"
          disabled={loading || code.length !== 6}
          className={`w-full py-3 rounded-lg font-semibold transition text-lg ${
            loading || code.length !== 6 ? "bg-gray-600 cursor-not-allowed" : "bg-primary hover:bg-primary-dark text-dark-bg"
          }`}
        >
          {loading ? t.verifying : t.verify}
        </button>

        <div className="flex items-center justify-between text-sm text-gray-400">
          <button
            type="button"
            onClick={availableChannels.length > 1 ? handleBackToChannel : handleBackToCredentials}
            className="hover:text-primary transition font-medium"
          >
            {t.back}
          </button>
          <button type="button" onClick={handleResend} disabled={loading} className="hover:text-primary transition font-medium disabled:opacity-50">
            {t.resend}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form
      onSubmit={handleLogin}
      className="w-full max-w-md bg-dark-card border border-dark-border rounded-xl p-8 shadow-lg space-y-5"
    >
      <div className="text-center mb-6">
        <h2 className="text-3xl font-bold text-white">{t.welcome}</h2>
        <p className="text-gray-400 mt-2">{t.signInTo}</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">{t.email}</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-2 bg-dark-bg border border-dark-border rounded-lg focus:outline-none focus:border-primary text-white placeholder-gray-500"
          placeholder="your@email.com"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">{t.password}</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-4 py-2 bg-dark-bg border border-dark-border rounded-lg focus:outline-none focus:border-primary text-white placeholder-gray-500"
          placeholder="••••••••"
          required
        />
      </div>

      {error && <p className="text-red-400 text-sm text-center bg-red-500 bg-opacity-10 border border-red-500 rounded-lg p-3">{error}</p>}

      <button
        type="submit"
        disabled={loading}
        className={`w-full py-3 rounded-lg font-semibold transition text-lg ${
          loading ? "bg-gray-600 cursor-not-allowed" : "bg-primary hover:bg-primary-dark text-dark-bg"
        }`}
      >
        {loading ? t.signingIn : t.signIn}
      </button>

      <div className="text-center text-sm text-gray-400">
        <button
          type="button"
          onClick={() => navigate("/forgot-password")}
          className="hover:text-primary transition font-medium"
        >
          {t.forgot}
        </button>
      </div>

      <div className="border-t border-dark-border pt-4 text-center text-sm text-gray-400">
        {t.noAccount}{" "}
        <button
          type="button"
          onClick={() => navigate("/register")}
          className="text-primary hover:text-primary-dark transition font-medium"
        >
          {t.createOne}
        </button>
      </div>
    </form>
  );
}
